import React, { useMemo, useState } from 'react'
import { Modal, ModalHeader, ModalBody, ModalFooter, UncontrolledTooltip } from 'reactstrap'
import AddPeopleModal from '../IncidentModals/AddPeopleModal'
import AddPartyEntityModal from '../IncidentModals/AddPartyEntityModal'
import PersonInfo from './PersonInfo'
import toTitleCase from '../../Functions/toTitleCase'
import seatArrangementImage from './seat-arrangement.png'
import './SeatArrangement.css'

const DRIVER_ROLES = ['VEHICLE_DRIVER', 'UNAUTHORIZED_DRIVER', 'EMPLOYEE', 'VENDOR']
const SEAT_KEYS = ['P1', 'P2', 'P3', 'P4']

const SEAT_DEFINITIONS = [
    { key: 'P2', label: 'Rear Left Passenger', addAction: 'addPassenger' },
    { key: 'P3', label: 'Rear Middle Passenger', addAction: 'addPassenger' },
    { key: 'P4', label: 'Rear Right Passenger', addAction: 'addPassenger' },
    { key: 'P1', label: 'Front Passenger', addAction: 'addPassenger' },
    { key: 'D1', label: 'Driver', addAction: 'addDriver' },
]

const getPassengerPositions = (metadata) => {
    if (!metadata) return {}
    if (typeof metadata === 'string') {
        try {
            return JSON.parse(metadata)?.passenger_positions || {}
        } catch {
            return {}
        }
    }
    return metadata.passenger_positions || {}
}

const SeatArrangement = ({ party, refetchQuery }) => {
    const [openModal, setOpenModal] = useState('')
    const hasRental = Boolean(party?.incident?.rental?.id)

    const peopleEdges = useMemo(
        () => party?.incidentpartypeopleSet?.edges ?? [],
        [party?.incidentpartypeopleSet?.edges]
    )

    const driverPeople = useMemo(
        () => peopleEdges.filter(p => DRIVER_ROLES.includes(p.node.role)),
        [peopleEdges]
    )

    const passengerPeople = useMemo(
        () => peopleEdges.filter(p => p.node.role === 'VEHICLE_PASSENGER'),
        [peopleEdges]
    )

    const hasVehicleDriver = useMemo(
        () => peopleEdges.some(p => p.node.role === 'VEHICLE_DRIVER'),
        [peopleEdges]
    )

    const passengerPositions = useMemo(
        () => getPassengerPositions(party?.metadata),
        [party?.metadata]
    )

    const resolvedPassengersBySeat = useMemo(() => {
        const passengerById = new Map(
            passengerPeople.map(person => [String(person.node.pk ?? person.node.id), person])
        )

        const usedPassengerIds = new Set()
        const result = {}

        Object.entries(passengerPositions).forEach(([passengerId, seatKey]) => {
            const normalizedId = String(passengerId)
            if (SEAT_KEYS.includes(seatKey) && passengerById.has(normalizedId) && !usedPassengerIds.has(normalizedId)) {
                result[seatKey] = passengerById.get(normalizedId)
                usedPassengerIds.add(normalizedId)
            }
        })

        return result
    }, [passengerPeople, passengerPositions])

    const seats = useMemo(() => SEAT_DEFINITIONS.map(seat => ({
        ...seat,
        person: seat.key === 'D1' ? driverPeople[0] : resolvedPassengersBySeat[seat.key],
    })), [driverPeople, resolvedPassengersBySeat])

    const handleSeatClick = (seat) => {
        if (seat.person?.node?.id) {
            setOpenModal(`view_${seat.key}`)
            return
        }
        setOpenModal(`${seat.addAction}_${seat.key}`)
    }

    return (
        <div className="seat-arrangement">
            <div className="seat-arrangement__canvas">
                <img src={seatArrangementImage} alt="Seat arrangement" className="seat-arrangement__image" />
                {seats.map(seat => (
                    <button
                        key={seat.key}
                        type="button"
                        className={`seat-arrangement__seat seat-arrangement__seat--${seat.key.toLowerCase()} ${seat.person ? 'is-filled' : ''}`}
                        onClick={() => handleSeatClick(seat)}
                        id={seat.key}
                    >
                        <span className="seat-arrangement__seat-label">
                            {seat.person?.node?.name || seat.label}
                        </span>
                        <UncontrolledTooltip target={seat.key}>
                            {seat.person ? 'View Details' : `Add ${seat.label}`}
                        </UncontrolledTooltip>
                    </button>
                ))}
            </div>

            <div className="seat-arrangement__legend">
                <div className="seat-arrangement__legend-item">
                    <span className="seat-arrangement__legend-dot seat-arrangement__legend-dot--available" />
                    Available Seat
                </div>
                <div className="seat-arrangement__legend-item">
                    <span className="seat-arrangement__legend-dot seat-arrangement__legend-dot--occupied" />
                    Occupied Seat
                </div>
            </div>

            {seats.map(
                seat =>
                    seat.person?.node?.id && (
                        <Modal key={`modal_${seat.key}`} isOpen={openModal === `view_${seat.key}`} toggle={() => setOpenModal('')} size="lg">
                            <ModalHeader>{toTitleCase(seat.person.node.role)} - {seat.person.node.name}</ModalHeader>
                            <ModalBody>
                                <PersonInfo person={seat.person.node} refetchQuery={refetchQuery} isDefault={party.isDefault} hasRental={hasRental} colSize={6} passengerPosition={seat.key.startsWith('P') ? seat.key : undefined} />
                            </ModalBody>
                            <ModalFooter>
                                <button type="button" className="btn btn-secondary" onClick={() => setOpenModal('')}>Close</button>
                            </ModalFooter>
                        </Modal>
                    )
            )}

            {openModal === 'addDriver_D1' &&
                (party.isDefault ? (
                    <AddPeopleModal
                        isOpen={true}
                        toggle={() => setOpenModal('')}
                        partyType="VEHICLE"
                        roles={!hasVehicleDriver ? DRIVER_ROLES : DRIVER_ROLES.filter(r => r !== 'VEHICLE_DRIVER')}
                        partyId={party.id}
                        refetchQuery={refetchQuery}
                    />
                ) : (
                    <AddPeopleModal
                        isOpen={true}
                        toggle={() => setOpenModal('')}
                        partyType="VEHICLE"
                        role="VEHICLE_DRIVER"
                        partyId={party.id}
                        refetchQuery={refetchQuery}
                    />
                ))}

            {openModal.startsWith('addPassenger_') && (
                <AddPartyEntityModal
                    isOpen={true}
                    toggle={() => setOpenModal('')}
                    entity="VEHICLE_PASSENGER"
                    passengerPosition={openModal.replace('addPassenger_', '')}
                    partyId={party?.id}
                    refetchQuery={refetchQuery}
                />
            )}
        </div>
    )
}

export default SeatArrangement
